import React, { useState } from 'react';

/**
 * LeadFilters Component
 *
 * Filter interface for leads with quick filter buttons and advanced filter options.
 * Includes collapsible advanced section for additional filters.
 *
 * @param {Object} filters - Current filter values
 * @param {function} onFilterChange - Callback when filters change
 * @param {Array} agents - List of agents (for manager filter)
 * @param {boolean} showAgentFilter - Whether to show agent filter (managers only)
 */
function LeadFilters({ filters, onFilterChange, agents = [], showAgentFilter = false }) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  function handleChange(e) {
    const { name, value } = e.target;
    onFilterChange({
      ...filters,
      [name]: value
    });
  }

  function handleClearFilters() {
    onFilterChange({
      status: '',
      propertyType: '',
      projectType: '',
      platform: '',
      search: '',
      heatLevel: '',
      agentId: '',
      dateFrom: '',
      dateTo: '',
      assignedFrom: '',
      assignedTo: '',
      lastActivityFrom: '',
      lastActivityTo: '',
      appointmentFrom: '',
      appointmentTo: ''
    });
  }

  return (
    <div className="card mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-display font-semibold text-gray-900">Filters</h3>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-sm text-tenq-gold hover:text-tenq-gold-dark transition-colors flex items-center gap-2"
          >
            <svg
              className={`w-4 h-4 transition-transform duration-200 ${showAdvanced ? 'rotate-180' : ''}`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
            {showAdvanced ? 'Hide' : 'Show'} Advanced
          </button>
          <button
            onClick={handleClearFilters}
            className="text-sm text-tenq-gold hover:text-tenq-gold-dark transition-colors"
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Quick Filter Buttons */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => onFilterChange({ ...filters, status: '' })}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
            filters.status === ''
              ? 'bg-tenq-gold text-white shadow-md'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          All Leads
        </button>
        <button
          onClick={() => onFilterChange({ ...filters, status: 'new' })}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
            filters.status === 'new'
              ? 'bg-tenq-gold text-white shadow-md'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          New
        </button>
        <button
          onClick={() => onFilterChange({ ...filters, status: 'appt_set' })}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
            filters.status === 'appt_set'
              ? 'bg-tenq-gold text-white shadow-md'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Appointments Set
        </button>
        <button
          onClick={() => onFilterChange({ ...filters, status: 'contact_attempted' })}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
            filters.status === 'contact_attempted'
              ? 'bg-tenq-gold text-white shadow-md'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Needs Follow-up
        </button>
      </div>

      {/* Quick Filters - Always Visible */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {/* Search */}
        <div>
          <label className="label-sm">Search</label>
          <input
            type="text"
            name="search"
            value={filters.search || ''}
            onChange={handleChange}
            className="input"
            placeholder="Name, phone, or email..."
          />
        </div>

        {/* Status Filter */}
        <div>
          <label className="label-sm">Status</label>
          <select
            name="status"
            value={filters.status || ''}
            onChange={handleChange}
            className="input"
          >
            <option value="">All Statuses</option>
            <option value="new">New</option>
            <option value="contact_attempted">Contact Attempted</option>
            <option value="contact_made">Contact Made</option>
            <option value="appt_set">Appointment Set</option>
            <option value="appt_no_show">Appointment No Show</option>
            <option value="appt_held">Appointment Held</option>
            <option value="pending_booking">Pending Booking</option>
            <option value="booked">Booked</option>
            <option value="system_discard">System Discard</option>
            <option value="manual_discard">Manual Discard</option>
          </select>
        </div>

        {/* Heat Level Filter */}
        <div>
          <label className="label-sm">Heat Level</label>
          <select
            name="heatLevel"
            value={filters.heatLevel || ''}
            onChange={handleChange}
            className="input"
          >
            <option value="">All Heat Levels</option>
            <option value="5">⭐⭐⭐⭐⭐ (5 Stars - Hottest)</option>
            <option value="4">⭐⭐⭐⭐ (4 Stars)</option>
            <option value="3">⭐⭐⭐ (3 Stars)</option>
            <option value="2">⭐⭐ (2 Stars)</option>
            <option value="1">⭐ (1 Star - Coldest)</option>
          </select>
        </div>
      </div>

      {/* Advanced Filters - Collapsible */}
      {showAdvanced && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200 animate-slideDown">
          {/* Property Type */}
          <div>
            <label className="label-sm">Property Type</label>
            <select
              name="propertyType"
              value={filters.propertyType || ''}
              onChange={handleChange}
              className="input"
            >
              <option value="">All Properties</option>
              <option value="hdb">HDB</option>
              <option value="condo">Condo</option>
              <option value="landed">Landed</option>
              <option value="commercial">Commercial</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Project Type */}
          <div>
            <label className="label-sm">Project Type</label>
            <select
              name="projectType"
              value={filters.projectType || ''}
              onChange={handleChange}
              className="input"
            >
              <option value="">All Projects</option>
              <option value="full_renovation">Full Renovation</option>
              <option value="partial_renovation">Partial Renovation</option>
              <option value="interior_design">Interior Design</option>
              <option value="consultation">Consultation</option>
            </select>
          </div>

          {/* Platform */}
          <div>
            <label className="label-sm">Platform</label>
            <select
              name="platform"
              value={filters.platform || ''}
              onChange={handleChange}
              className="input"
            >
              <option value="">All Platforms</option>
              <option value="manual">Manual</option>
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
              <option value="tiktok">TikTok</option>
              <option value="google">Google</option>
              <option value="referral">Referral</option>
            </select>
          </div>

          {/* Agent Filter (for managers) */}
          {showAgentFilter && agents.length > 0 && (
            <div>
              <label className="label-sm">Assigned Agent</label>
              <select
                name="agentId"
                value={filters.agentId || ''}
                onChange={handleChange}
                className="input"
              >
                <option value="">All Agents</option>
                {agents.map(agent => (
                  <option key={agent.id} value={agent.id}>
                    {agent.fullName} ({agent.nickname})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Created Date From */}
          <div>
            <label className="label-sm">Created From</label>
            <input
              type="date"
              name="dateFrom"
              value={filters.dateFrom || ''}
              onChange={handleChange}
              className="input"
            />
          </div>

          {/* Created Date To */}
          <div>
            <label className="label-sm">Created To</label>
            <input
              type="date"
              name="dateTo"
              value={filters.dateTo || ''}
              onChange={handleChange}
              className="input"
            />
          </div>

          {/* Assigned On From */}
          <div>
            <label className="label-sm">Assigned On (From)</label>
            <input
              type="date"
              name="assignedFrom"
              value={filters.assignedFrom || ''}
              onChange={handleChange}
              className="input"
            />
          </div>

          {/* Assigned On To */}
          <div>
            <label className="label-sm">Assigned On (To)</label>
            <input
              type="date"
              name="assignedTo"
              value={filters.assignedTo || ''}
              onChange={handleChange}
              className="input"
            />
          </div>

          {/* Last Activity From */}
          <div>
            <label className="label-sm">Last Activity (From)</label>
            <input
              type="date"
              name="lastActivityFrom"
              value={filters.lastActivityFrom || ''}
              onChange={handleChange}
              className="input"
            />
          </div>

          {/* Last Activity To */}
          <div>
            <label className="label-sm">Last Activity (To)</label>
            <input
              type="date"
              name="lastActivityTo"
              value={filters.lastActivityTo || ''}
              onChange={handleChange}
              className="input"
            />
          </div>

          {/* Appointment From */}
          <div>
            <label className="label-sm">Appointment (From)</label>
            <input
              type="date"
              name="appointmentFrom"
              value={filters.appointmentFrom || ''}
              onChange={handleChange}
              className="input"
            />
          </div>

          {/* Appointment To */}
          <div>
            <label className="label-sm">Appointment (To)</label>
            <input
              type="date"
              name="appointmentTo"
              value={filters.appointmentTo || ''}
              onChange={handleChange}
              className="input"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default LeadFilters;
